import { type FC, type ReactNode } from 'react'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../components/form'
import { Input } from '../../components/input'
import { Button } from '../../components/button'

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"

import MediaUploader from '../MediaUploader'

import { LoaderCircle } from 'lucide-react'

import ErrorComponent from '../ErrorComponent'
import axios from 'axios'

import CountrySelect from '../CountrySelect'
import { useEffect } from 'react'
import { detectCountryCode } from '../../lib/detectCountryCode'


const AddStaffFormSchema = z.object({
    staffName: z.string().trim().min(1, 'This field is required').max(100, "Staff Name cannot exceed 100 characters."),
    emailID: z
        .string()
        .trim()
        .email('Invalid email ID')
        .min(1, 'This field is required')
        .max(255, "Email cannot exceed 255 characters."),
    phoneNumber: z
        .string()
        .trim()
        .min(1, 'This field is required')
        .refine((val) => val.replace(/\s/g, '').length > 0, {
            message: 'This field is required'
        }).refine((val) => val.length <= 20, { message: 'Phone number cannot exceed 20 characters' }),
    phoneNumberCode: z.string().trim().min(1, 'This field is required'),
    jobTitle: z
        .string()
        .trim()
        .min(1, 'This field is required')
        .refine((val) => val.replace(/\s/g, '').length > 0, {
            message: 'This field is required'
        }).refine((val) => val.length <= 100, { message: 'Job Title cannot exceed 100 characters' }),
    profilePic: z.string().trim().min(1, 'Please upload a profile picture')
})

type AddStaffType = {
    children?: ReactNode,
    onSubmit: (values: z.infer<typeof AddStaffFormSchema>) => void,
    handleSendToUpload: (files: File[]) => Promise<string[]>,
    mediaLoading: boolean,
    isError?: boolean,
    isLoading?: boolean,
    error?: Error | null,
    existedValues?: z.infer<typeof AddStaffFormSchema>,
}

const AddStaffForm: FC<AddStaffType> = ({ onSubmit, handleSendToUpload, mediaLoading, isError, isLoading, error, existedValues }) => {
    const form = useForm<z.infer<typeof AddStaffFormSchema>>({
        resolver: zodResolver(AddStaffFormSchema),
        defaultValues: {
            staffName: "",
            emailID: "",
            phoneNumber: '',
            jobTitle: "",
            profilePic: "",
            phoneNumberCode: "+1",
            ...existedValues
        },
    })

    const phoneNumber = form.watch('phoneNumber');
    
    useEffect(() => {
        if (phoneNumber && phoneNumber.replace(/\D/g, '').length >= 2) {
            const detected = detectCountryCode(phoneNumber);
            if (detected) {
                form.setValue('phoneNumberCode', detected);
            }
        }
    }, [phoneNumber, form]);

    const isAxiosError = axios.isAxiosError(error)
    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <FormField
                    control={form.control}
                    name="staffName"
                    render={({ field }) => (
                        <FormItem className='mb-0 w-full'>
                            <FormLabel className='text-[#38404D]'>Staff Full Name <span className='text-[#16111]'>*</span></FormLabel>
                            <FormControl className='mt-[6px]'>
                                <Input placeholder="John Doe" {...field} maxLength={100} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <div className='grid grid-cols-1 md:grid-cols-2 gap-[18px] h-full mb-0'>
                    <div>
                        <FormField
                            control={form.control}
                            name="profilePic"
                            render={({ field }) => (
                                <FormItem className='mb-0 w-full mt-[16px] h-full flex flex-col'>
                                    <FormLabel className='text-[#38404D]'>Profile Picture<span className='text-[#16111]'>*</span></FormLabel>
                                    <FormControl className='mt-[6px] h-full'>
                                        <MediaUploader handleSendToUpload={async (files) => {
                                            const urls = await handleSendToUpload(files)
                                            form.setValue("profilePic", urls[0]!)
                                        }} onRemove={() => form.setValue("profilePic", "")} value={field.value} mediaLoading={mediaLoading} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div>
                        <FormField
                            control={form.control}
                            name="emailID"
                            render={({ field }) => (
                                <FormItem className=' mt-[16px] mb-0 w-full'>
                                    <FormLabel className='text-[#38404D]'>Email ID <span className='text-[#16111]'>*</span></FormLabel>
                                    <FormControl className='mt-[6px]'>
                                        <Input placeholder="john@work.com" {...field} type='email' maxLength={255} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="phoneNumber"
                            render={({ field }) => (
                                <FormItem className='mt-[16px] mb-0'>
                                    <FormLabel className='text-[#38404D]'>Phone Number <span className='text-[#16111]'>*</span></FormLabel>
                                    <FormControl>
                                        <div className='relative w-full'>
                                            <FormField
                                                control={form.control}
                                                name="phoneNumberCode"
                                                render={({ field }) => (
                                                    <CountrySelect field={field} />
                                                )}
                                            />
                                            <Input placeholder="8012345678" {...field} className={`pl-[100px]`} maxLength={20} />
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="jobTitle"
                            render={({ field }) => (
                                <FormItem className='mb-0 w-full mt-[16px]'>
                                    <FormLabel className='text-[#38404D]'>Job Title <span className='text-[#16111]'>*</span></FormLabel>
                                    <FormControl className='mt-[6px]'>
                                        <Input placeholder="Salesperson" {...field} maxLength={100} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                </div>


                <div className='mt-[16px]'>
                    {
                        isError && <ErrorComponent message={isAxiosError ? error?.response?.data?.error : ''} />
                    }
                </div>

                <div className='mt-[32px] flex items-center justify-end gap-[16px]'>
                    <Button type="submit" className='w-fit ' isLoading={isLoading}>
    {existedValues ? 'Update staff' : 'Add new staff'}
</Button>
                    {/* <Button type="submit" className='w-fit' variant={'white'}>Save as Draft</Button> */}
                </div>

            </form>
        </Form>
    )

}

export { AddStaffForm, AddStaffFormSchema }